import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: `1px solid ${tokens.edge}`,
    padding: '13px 16px',
    color: tokens.textHi,
    fontSize: 15,
    fontFamily: tokens.fontBody,
    outline: 'none',
    boxSizing: 'border-box',
    transition: `border-color 300ms ${tokens.ease}`,
  };

  const focusIn = e => { e.currentTarget.style.borderColor = tokens.accent; };
  const focusOut = e => { e.currentTarget.style.borderColor = tokens.edge; };

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.ground,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px',
      position: 'relative',
    }}>
      {/* Atmospheric glow */}
      <div className="gl-atmosphere" />
      <div className="gl-grain" />

      <div style={{
        width: '100%', maxWidth: 380,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        position: 'relative', zIndex: 2,
        animation: `gl-fade-up 600ms ${tokens.ease}`,
      }}>

        {/* Wordmark */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 9,
          fontFamily: tokens.fontMono, fontSize: 11,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: tokens.accent, fontWeight: 500,
          marginBottom: 56,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: tokens.accent,
            boxShadow: `0 0 10px ${tokens.accent}`,
            animation: 'gl-breathe 3.5s ease-in-out infinite',
          }} />
          ghostlight
        </div>

        {/* Heading */}
        <h1 style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 32, fontWeight: tokens.displayWeight,
          letterSpacing: tokens.displayTracking,
          color: tokens.textHi,
          margin: 0, textAlign: 'center',
        }}>
          Welcome back.
        </h1>

        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 15, color: tokens.textMid,
          margin: '12px 0 0', textAlign: 'center',
        }}>
          The light's still on.
        </p>

        {/* Error */}
        {error && (
          <div style={{
            width: '100%', marginTop: 28,
            padding: '12px 16px',
            color: tokens.danger,
            fontSize: 13, fontFamily: tokens.fontBody,
            border: `1px solid ${tokens.danger}33`,
            background: `${tokens.danger}0a`,
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 40 }}>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block',
              fontFamily: tokens.fontBody, fontSize: 11,
              color: tokens.textLow, letterSpacing: '0.14em',
              textTransform: 'uppercase', marginBottom: 8,
            }}>Email</label>
            <input
              type="email" required
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              style={inputStyle}
              onFocus={focusIn} onBlur={focusOut}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: 'block',
              fontFamily: tokens.fontBody, fontSize: 11,
              color: tokens.textLow, letterSpacing: '0.14em',
              textTransform: 'uppercase', marginBottom: 8,
            }}>Password</label>
            <input
              type="password" required
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Your password"
              style={inputStyle}
              onFocus={focusIn} onBlur={focusOut}
            />
          </div>

          <div style={{ textAlign: 'right', marginBottom: 32 }}>
            <Link to="/forgot-password" style={{
              color: tokens.accent,
              fontSize: 12, fontFamily: tokens.fontBody,
              letterSpacing: '0.04em',
              textDecoration: 'none',
            }}>
              Forgot password?
            </Link>
          </div>

          <button type="submit" disabled={submitting}
            style={{
              width: '100%',
              border: `1px solid ${tokens.accent}`,
              background: tokens.accentGlow,
              color: tokens.accent,
              fontFamily: tokens.fontBody, fontWeight: 500,
              fontSize: 14, letterSpacing: '0.04em',
              padding: '15px',
              cursor: submitting ? 'default' : 'pointer',
              transition: `background 300ms ${tokens.ease}`,
              opacity: submitting ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = `${tokens.accent}22`; }}
            onMouseLeave={e => { e.currentTarget.style.background = tokens.accentGlow; }}
          >
            {submitting ? 'Signing in\u2026' : 'Sign in'}
          </button>
        </form>

        {/* Divider */}
        <div style={{ width: '100%', height: 1, background: tokens.edge, margin: '36px 0' }} />

        {/* Bottom links */}
        <div style={{ textAlign: 'center' }}>
          <p style={{
            fontFamily: tokens.fontBody, fontSize: 13,
            color: tokens.textMid, margin: 0,
          }}>
            New here?{' '}
            <Link to="/register" style={{ color: tokens.accent, textDecoration: 'none' }}>Create an account</Link>
          </p>

          <Link to="/welcome" style={{
            display: 'inline-block', marginTop: 20,
            color: tokens.textLow, fontSize: 12,
            fontFamily: tokens.fontBody, letterSpacing: '0.04em',
            textDecoration: 'none',
          }}>
            &larr; Back to GhostLight
          </Link>
        </div>
      </div>
    </div>
  );
}
